// Tableau field identity + normalization contract (shared across UI/worker/parity tooling).
//
// Goal: deterministically map Tableau-visible field refs (captions, bracketed refs, internal tokens)
// to stable keys (and ultimately to extract column ids).
//
// This module intentionally mirrors (and should stay consistent with) the Rust-side contract
// in `server/src/tableau/field_identity.rs`.
//
// Notes:
// - Tableau internal tokens often look like: `none:Field Name:nk`, `sum:Sales:qk`, `win:usr:Calc:qk:2`
// - Tableau also uses special tokens like `:Measure Names` / `Measure Names`
// - Datasource qualification uses bracketed refs: `[ds].[Field]`

import {
  isFieldMappingConsoleEnabled,
  isFieldMappingDebugEnabled,
  isFieldMappingVerboseEnabled,
  logFieldMapping,
  pushFieldMappingTrace,
} from '../utils/fieldMappingDebug'

// Until datasource/field identity is fully canonicalized end-to-end, prefer hard failures
// over heuristic column fallbacks so mapping gaps are visible and fixable at the source.
const FAIL_FAST_FIELD_MAPPING = true
let _resolveFieldVerboseNoticeShown = false

export type ParsedTableauFieldRef = {
  datasource: string | null
  token: string
}

export type ExtractFieldManifestEntry = {
  field_id?: string
  token?: string
  caption?: string
  column_name?: string
  calc_formula?: string
  calc_formula_raw?: string
  calc_duckdb_sql?: string
  is_calc?: boolean
  source_kind?: string
}

export type ExtractFieldManifest = {
  version?: number
  datasource_name?: string
  extract_columns?: string[]
  fields?: ExtractFieldManifestEntry[]
}

const FIELD_IDENTITY_STRING_CACHE_MAX = 20000
const bracketPartsCache = new Map<string, string[]>()
const cleanBracketedRefCache = new Map<string, string>()
const normalizedTokenCache = new Map<string, string>()
const strippedSuffixCache = new Map<string, string>()
const normalizedKeyCache = new Map<string, string>()
const normalizedStrictKeyCache = new Map<string, string>()
const templateParamSafeKeyCache = new Map<string, string>()
const displayProvenanceSuffixCache = new Map<string, string>()

const setBoundedCache = <T>(cache: Map<string, T>, key: string, value: T) => {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  if (cache.size > FIELD_IDENTITY_STRING_CACHE_MAX) {
    const oldest = cache.keys().next().value
    if (typeof oldest === 'string') cache.delete(oldest)
  }
}

const workbookAliasMapCache = new WeakMap<object, Map<string, string>>()
const workbookInternalFieldMapCache = new WeakMap<object, Map<string, string>>()
const availableColumnMapCache = new WeakMap<object, Map<string, string>>()
const manifestColumnMapCache = new WeakMap<object, Map<string, string>>()
const manifestCalcIdentityCache = new WeakMap<object, Set<string>>()

export function extractBracketedParts(raw: string): string[] {
  const s = String(raw ?? '')
  const cached = bracketPartsCache.get(s)
  if (cached) return [...cached]
  const out: string[] = []
  let i = s.indexOf('[')
  while (i >= 0 && i < s.length) {
    const j = s.indexOf(']', i + 1)
    if (j < 0) break
    if (j > i + 1) out.push(s.slice(i + 1, j))
    i = s.indexOf('[', j + 1)
  }
  setBoundedCache(bracketPartsCache, s, out)
  return out
}

export function parseTableauFieldRef(raw: string): ParsedTableauFieldRef | null {
  const s = String(raw ?? '').trim()
  const parts = extractBracketedParts(s)
  if (!parts.length) return null
  if (parts.length === 1) return { datasource: null, token: parts[0] || '' }
  if (parts.length === 2) {
    const qualified = s.match(/\[([^\]]+)\]\s*\.\s*\[([^\]]+)\]/)
    if (qualified) {
      const datasource = String(qualified[1] ?? '').trim()
      const token = String(qualified[2] ?? '').trim()
      if (datasource && token) return { datasource, token }
    }
  }
  return null
}

export function cleanBracketedFieldRef(raw: string): string {
  // Examples:
  // - "[Sales]" -> "Sales"
  // - "[ds].[Sales]" -> "Sales"
  // - "SUM([ds].[Sales])" -> "Sales" (best-effort: last bracketed part)
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const cached = cleanBracketedRefCache.get(s)
  if (typeof cached === 'string') return cached
  const parts = extractBracketedParts(s)
  if (parts.length) {
    const last = String(parts[parts.length - 1] ?? '').trim()
    const out = last || s
    setBoundedCache(cleanBracketedRefCache, s, out)
    return out
  }
  // Fallback: strip a single pair of outer brackets if present
  if (s.startsWith('[') && s.endsWith(']')) {
    const out = s.slice(1, -1).trim()
    setBoundedCache(cleanBracketedRefCache, s, out)
    return out
  }
  // Fallback: qualifier split without bracket parsing
  const segs = s.split('.').map((p) => p.trim()).filter(Boolean)
  const out = (segs[segs.length - 1] || s).trim()
  setBoundedCache(cleanBracketedRefCache, s, out)
  return out
}

const DISPLAY_PROVENANCE_SUFFIXES = new Set([
  'csv',
  'data source',
  'datasource',
  'excel',
  'extract',
  'google',
  'local',
  'published',
  'server',
  'sheet',
  'sheets',
  'sql',
  'text',
])

export function stripDisplayProvenanceSuffix(raw: string): string {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const cached = displayProvenanceSuffixCache.get(s)
  if (typeof cached === 'string') return cached

  const match = s.match(/^(.*\S)\s+\(([^()]+)\)\s*$/)
  if (!match) {
    setBoundedCache(displayProvenanceSuffixCache, s, s)
    return s
  }
  const base = String(match[1] ?? '').trim()
  const suffix = String(match[2] ?? '').trim().toLowerCase()
  const hasPriorQualifier = /\([^()]+\)\s*$/.test(base)
  const shouldStrip = hasPriorQualifier || DISPLAY_PROVENANCE_SUFFIXES.has(suffix)
  const out = shouldStrip && base ? base : s
  setBoundedCache(displayProvenanceSuffixCache, s, out)
  return out
}

export function normalizeTableauDatasourceKey(raw: string): string {
  // De-noise datasource refs like:
  // - "sqlproxy.abc123 (copy 2)" -> "sqlproxy.abc123 (copy 2)"
  // - "Data Source (copy)" -> "data source (copy)"
  let s = String(raw ?? '').trim()
  if (!s) return ''
  // Strip outer brackets if present
  if (s.startsWith('[') && s.endsWith(']')) s = s.slice(1, -1).trim()
  if (/^sqlproxy\./i.test(s)) return s.toLowerCase()
  if (/^sqlproxy$/i.test(s)) return 'sqlproxy'
  return s.toLowerCase()
}

const datasourceIdKeys = [
  'server-ds-id',
  'server-datasource-id',
  'datasource-id',
  'repository-datasource-id',
  'server-ds-luid',
  'datasource-luid',
]
const datasourceIdKeySet = new Set(datasourceIdKeys.map((k) => k.toLowerCase()))

export function normalizeDatasourceId(raw: string | null | undefined): string {
  return String(raw ?? '').trim().toLowerCase()
}

export function extractDatasourceIdFromName(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim()
  if (!s) return null
  if (s.toLowerCase().startsWith('id:')) {
    const id = s.slice(3).trim()
    return id || null
  }
  return null
}

function extractDatasourceIdFromAttrs(attrs: any): string | null {
  if (!attrs) return null
  if (Array.isArray(attrs)) {
    for (const entry of attrs) {
      const key = String(entry?.key ?? entry?.name ?? entry?.id ?? '').trim().toLowerCase()
      if (!key) continue
      if (!datasourceIdKeySet.has(key)) continue
      const val = String(entry?.value ?? entry?.val ?? entry?.data ?? '').trim()
      if (val) return val
    }
    return null
  }
  if (typeof attrs === 'object') {
    for (const [keyRaw, value] of Object.entries(attrs)) {
      const key = String(keyRaw ?? '').trim().toLowerCase()
      if (!key || !datasourceIdKeySet.has(key)) continue
      const val = String(value ?? '').trim()
      if (val) return val
    }
  }
  return null
}

export function getDatasourceIdFromConnections(connections: any[] | null | undefined): string | null {
  if (!Array.isArray(connections)) return null
  for (const conn of connections) {
    const attrs = (conn as any)?.attrs ?? (conn as any)?.attributes ?? (conn as any)?.attr ?? null
    const id = extractDatasourceIdFromAttrs(attrs)
    if (id) return id
  }
  return null
}

export function normalizeTableauFieldToken(token: string): string {
  // Mirrors server `normalize_tableau_field_token` semantics.
  //
  // Examples:
  // - `:Measure Names` -> `Measure Names`
  // - `none:Field Supervisor:nk` -> `Field Supervisor`
  // - `sum:Sales:qk` -> `Sales`
  // - `win:usr:Calculation_123:qk:2` -> `Calculation_123`
  let t = String(token ?? '').trim()
  if (!t) return t
  const cached = normalizedTokenCache.get(t)
  if (typeof cached === 'string') return cached
  if (t.startsWith(':')) t = t.slice(1).trim()
  // Tableau can escape percent signs inside member tokens (for example `Haircut PY \% Diff`).
  // Canonicalize those escapes here so token identity matches the field-dictionary entries.
  t = t.replace(/\\+%/g, '%')

  // Only apply colon-based normalization when this looks like a Tableau internal token.
  const looksInternal =
    t.includes(':') &&
    (/^(none|usr|twk|win|attr|sum|avg|min|max|count|countd|ctd|t(?:yr|mn|wk|dy|qr)|tqr|tyr|tmn|twk|tdy|tiwk|iyr|iwk|iwd|yr|mn|wk|dy|qr|wd|pcto|pcdf):/i.test(t) ||
      /:(nk|ok|qk|sk)(:\d+)?$/i.test(t) ||
      /:\d+$/i.test(t))
  if (!looksInternal) {
    setBoundedCache(normalizedTokenCache, String(token ?? '').trim(), t)
    return t
  }

  const parts = t.split(':')
  if (!parts.length) return t

  // Include common date-part prefixes that appear in Tableau shelf tokens:
  // - yr:Created Date
  // - mn:Created Date
  // - wk:...
  // - dy:...
  const lead = new Set([
    'none',
    'usr',
    'twk',
    'win',
    'attr',
    'sum',
    'avg',
    'min',
    'max',
    'count',
    'countd',
    'ctd',
    'tqr',
    'tyr',
    'tmn',
    'twk',
    'tdy',
    'tiwk',
    'iyr',
    'iwk',
    'iwd',
    'yr',
    'mn',
    'wk',
    'dy',
    'qr',
    'wd',
    'pcto',
    'pcdf',
  ])
  let start = 0
  while (start < parts.length && lead.has(String(parts[start] ?? '').trim().toLowerCase())) {
    start++
  }

  let end = parts.length
  // Drop trailing numeric-only segments (Tableau often appends ":2", ":3", etc)
  while (end > start && /^[0-9]+$/.test(String(parts[end - 1] ?? '').trim())) {
    end--
  }
  // Drop trailing instance-kind suffixes
  const tail = new Set(['nk', 'ok', 'qk', 'sk'])
  while (end > start && tail.has(String(parts[end - 1] ?? '').trim().toLowerCase())) {
    end--
  }

  if (start >= end) {
    setBoundedCache(normalizedTokenCache, String(token ?? '').trim(), t)
    return t
  }
  const out = parts.slice(start, end).join(':').trim()
  setBoundedCache(normalizedTokenCache, String(token ?? '').trim(), out)
  return out
}

export function stripTableauAutoGeneratedSuffixes(name: string): string {
  // Remove common Tableau "noise" suffixes used for disambiguation/copies.
  // Keep this conservative: only remove very long numeric suffixes.
  let s = String(name ?? '').trim()
  if (!s) return s
  const cached = strippedSuffixCache.get(s)
  if (typeof cached === 'string') return cached
  // Remove "(copy)" tokens anywhere (Tableau often embeds before a numeric suffix)
  s = s.replace(/\s*\(copy(?:\s*\d+)?\)\s*/gi, '').trim()
  // Remove Tableau-generated suffixes like "(generated)"
  s = s.replace(/\s*\(\s*generated\s*\)\s*$/gi, '').trim()
  // Remove redundant suffixes like "Order (Order)"
  const parenMatch = s.match(/^(.*)\(([^)]+)\)\s*$/)
  if (parenMatch) {
    const base = String(parenMatch[1] ?? '').trim()
    const suffix = String(parenMatch[2] ?? '').trim()
    if (base && suffix && base.toLowerCase() === suffix.toLowerCase()) {
      s = base
    }
  }
  // Remove huge numeric suffixes like `_200410188171804676`
  s = s.replace(/_(\d{8,})$/g, '').trim()
  setBoundedCache(strippedSuffixCache, String(name ?? '').trim(), s)
  return s
}

export function normalizeTableauFieldKey(raw: string): string {
  // Produces a stable lookup key suitable for matching:
  // - spec field_name ("Time Period")
  // - column name tokens ("none:Time Period (copy)_...:ok")
  // - bracketed refs ("[DS].[none:Time Period...:ok]")
  const rawText = String(raw ?? '')
  const cached = normalizedKeyCache.get(rawText)
  if (typeof cached === 'string') return cached
  const cleaned = cleanBracketedFieldRef(rawText)
  const normalized = normalizeTableauFieldToken(cleaned)
  const normTrim = normalized.trim()
  // Preserve numeric suffixes for Calculation_* tokens; they are unique identifiers.
  const isCalcToken = /^calculation_\d+$/i.test(normTrim)
  const denoised = isCalcToken ? normTrim : stripTableauAutoGeneratedSuffixes(normTrim)
  const out = denoised.trim().toLowerCase()
  setBoundedCache(normalizedKeyCache, rawText, out)
  return out
}

export function normalizeTableauFieldKeyStrict(raw: string): string {
  // Stable key that preserves Tableau-generated suffixes (copy/numeric disambiguators).
  // Use for identity-sensitive paths where suffix collapsing causes collisions.
  const rawText = String(raw ?? '')
  const cached = normalizedStrictKeyCache.get(rawText)
  if (typeof cached === 'string') return cached
  const cleaned = cleanBracketedFieldRef(rawText)
  const normalized = normalizeTableauFieldToken(cleaned)
  const out = normalized.trim().toLowerCase()
  setBoundedCache(normalizedStrictKeyCache, rawText, out)
  return out
}

const workbookParameterKeySetCache = new WeakMap<object, Set<string>>()

const getWorkbookParameterKeys = (workbookIr: any | null | undefined): Set<string> => {
  if (!workbookIr || typeof workbookIr !== 'object') return new Set<string>()
  const cached = workbookParameterKeySetCache.get(workbookIr as object)
  if (cached) return cached
  const out = new Set<string>()
  const params = Array.isArray((workbookIr as any)?.parameters) ? ((workbookIr as any).parameters as any[]) : []
  for (const entry of params) {
    for (const raw of [
      (entry as any)?.name,
      (entry as any)?.parameter_name,
      (entry as any)?.caption,
      (entry as any)?.attrs?.name,
      (entry as any)?.attrs?.caption,
      (entry as any)?.attrs?.['parameter-name'],
    ]) {
      const text = String(raw ?? '').trim()
      if (!text) continue
      const keys = [
        normalizeTableauFieldKey(text),
        normalizeTableauFieldKey(cleanBracketedFieldRef(text)),
      ]
      for (const key of keys) {
        if (key) out.add(key)
      }
    }
  }
  workbookParameterKeySetCache.set(workbookIr as object, out)
  return out
}

export function isWorkbookParameterFieldEntry(entry: any, workbookIr?: any | null): boolean {
  if (!entry || typeof entry !== 'object') return false
  const dsKey = normalizeTableauDatasourceKey(String((entry as any)?.datasource ?? ''))
  if (dsKey === 'parameters') return true

  const explicitParameterRef = String(
    (entry as any)?.parameter_name ??
    (entry as any)?.attrs?.['parameter-name'] ??
    '',
  ).trim()
  if (explicitParameterRef) return true

  const workbookParamKeys = getWorkbookParameterKeys(workbookIr ?? null)
  if (!workbookParamKeys.size) return false
  for (const raw of [
    (entry as any)?.name,
    (entry as any)?.caption,
    (entry as any)?.parameter_name,
    (entry as any)?.attrs?.name,
    (entry as any)?.attrs?.caption,
    (entry as any)?.attrs?.['parameter-name'],
  ]) {
    const text = String(raw ?? '').trim()
    if (!text) continue
    const keys = [
      normalizeTableauFieldKey(text),
      normalizeTableauFieldKey(cleanBracketedFieldRef(text)),
    ]
    for (const key of keys) {
      if (key && workbookParamKeys.has(key)) return true
    }
  }
  return false
}

const hashTemplateParamKey = (input: string): number => {
  let hash = 0
  const s = String(input ?? '')
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0
  }
  return hash
}

export function buildTemplateParamSafeKey(raw: string): string {
  const rawText = String(raw ?? '').trim()
  if (!rawText) return 'p_empty'
  const cached = templateParamSafeKeyCache.get(rawText)
  if (typeof cached === 'string') return cached
  const normalized =
    normalizeTableauFieldKeyStrict(rawText) ||
    cleanBracketedFieldRef(rawText).trim().toLowerCase() ||
    rawText.toLowerCase()
  const base = String(normalized ?? '')
    .trim()
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/^_+/, '')
    .replace(/_+$/, '')
    || 'p'
  // Hash the canonical parameter identity so datasource-qualified aliases
  // bind against the same placeholder, while distinct Tableau parameters like
  // "Date Parameter" vs "Date Parameter (copy)" still remain distinct.
  const suffix = Math.abs(hashTemplateParamKey(normalized)).toString(36)
  const out = `${base}__${suffix}`
  setBoundedCache(templateParamSafeKeyCache, rawText, out)
  return out
}

export function buildWorkbookFieldAliasToBaseColumnMap(workbookIr: any | null | undefined): Map<string, string> {
  // Maps many Tableau-facing aliases -> base column id (best-effort).
  if (workbookIr && typeof workbookIr === 'object') {
    const cached = workbookAliasMapCache.get(workbookIr as object)
    if (cached) return cached
  }
  const out = new Map<string, { col: string; score: number }>()
  const put = (aliasRaw: any, colRaw: any, score: number) => {
    const col = String(colRaw ?? '').trim()
    if (!col) return
    const alias = String(aliasRaw ?? '').trim()
    if (!alias) return
    const k = normalizeTableauFieldKey(alias)
    if (!k) return
    const prev = out.get(k)
    if (!prev || score > prev.score) out.set(k, { col, score })
  }

  const fd = workbookIr && typeof workbookIr === 'object' ? (workbookIr as any).field_dictionary : null
  const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
  const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []

  const calculatedColKeys = new Set<string>()
  for (const c of cols) {
    const name = String(c?.name ?? '').trim()
    if (!name) continue
    const formula = String(c?.calculation?.formula ?? '').trim()
    if (formula) {
      const k = normalizeTableauFieldKey(name)
      if (k) calculatedColKeys.add(k)
    }
    const caption = String(c?.caption ?? '').trim()
    // Strong matches
    if (caption) put(caption, name, 100)
    put(name, name, 90)
    // Normalized matches
    put(normalizeTableauFieldToken(name), name, 80)
    if (caption) put(normalizeTableauFieldToken(caption), name, 70)
    // De-noised variants
    put(stripTableauAutoGeneratedSuffixes(normalizeTableauFieldToken(name)), name, 60)
    if (caption) put(stripTableauAutoGeneratedSuffixes(normalizeTableauFieldToken(caption)), name, 50)
  }

  for (const ci of cis) {
    const base = String(ci?.column ?? '').trim()
    if (!base) continue
    // Column instances for calculated fields often point `column` at a physical dependency
    // (e.g. haircutcount), while `base_field_id` identifies the actual calculated field token.
    // If we map alias -> physical dependency, calculated measures like "Cuts / Day" can collapse
    // to raw columns and skip formula compilation, causing severe data parity gaps.
    const baseFieldIdRaw = String((ci as any)?.base_field_id ?? (ci as any)?.baseFieldId ?? '').trim()
    const baseFieldKey = baseFieldIdRaw ? normalizeTableauFieldKey(baseFieldIdRaw) : ''
    const baseFieldClean = baseFieldIdRaw ? cleanBracketedFieldRef(baseFieldIdRaw) : ''
    const baseIsCalc =
      Boolean(baseFieldKey) &&
      (
        baseFieldKey === 'calculation' ||
        baseFieldKey.startsWith('calculation_') ||
        calculatedColKeys.has(baseFieldKey)
      )
    const targetBase = (baseIsCalc && baseFieldClean) ? baseFieldClean : base
    const nm = String(ci?.name ?? '').trim()
    const cap = String(ci?.caption ?? '').trim()
    if (nm) put(nm, targetBase, 75)
    if (cap) put(cap, targetBase, 65)
    if (nm) put(normalizeTableauFieldToken(nm), targetBase, 55)
    if (cap) put(normalizeTableauFieldToken(cap), targetBase, 45)
  }

  // Special fields (Tableau semantics; not physical extract columns).
  put(':Measure Names', 'Measure Names', 1000)
  put('Measure Names', 'Measure Names', 1000)
  put(':Measure Values', 'Measure Values', 1000)
  put('Measure Values', 'Measure Values', 1000)
  put(':Multiple Values', 'Measure Values', 1000)
  put('Multiple Values', 'Measure Values', 1000)

  const flat = new Map<string, string>()
  for (const [k, v] of out.entries()) flat.set(k, v.col)
  if (workbookIr && typeof workbookIr === 'object') {
    workbookAliasMapCache.set(workbookIr as object, flat)
  }
  return flat
}

function buildWorkbookInternalFieldToBaseColumnMap(workbookIr: any | null | undefined): Map<string, string> {
  if (workbookIr && typeof workbookIr === 'object') {
    const cached = workbookInternalFieldMapCache.get(workbookIr as object)
    if (cached) return cached
  }
  const out = new Map<string, { col: string; score: number }>()
  const put = (aliasRaw: any, colRaw: any, score: number) => {
    const col = String(colRaw ?? '').trim()
    if (!col) return
    const alias = String(aliasRaw ?? '').trim()
    if (!alias) return
    const k = normalizeTableauFieldKeyStrict(alias)
    if (!k) return
    const prev = out.get(k)
    if (!prev || score > prev.score) out.set(k, { col, score })
  }

  const fd = workbookIr && typeof workbookIr === 'object' ? (workbookIr as any).field_dictionary : null
  const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
  const cis: any[] = Array.isArray(fd?.column_instances) ? fd.column_instances : []

  const calculatedColKeys = new Set<string>()
  for (const c of cols) {
    const name = String(c?.name ?? '').trim()
    if (!name) continue
    const formula = String(c?.calculation?.formula ?? '').trim()
    if (!formula) continue
    const k = normalizeTableauFieldKey(name)
    if (k) calculatedColKeys.add(k)
  }

  for (const c of cols) {
    const name = String(c?.name ?? '').trim()
    if (!name) continue
    put(name, name, 100)
    const token = normalizeTableauFieldToken(name)
    if (token && token !== name) put(token, name, 90)
  }

  for (const ci of cis) {
    const base = String(ci?.column ?? '').trim()
    if (!base) continue
    const baseFieldIdRaw = String((ci as any)?.base_field_id ?? (ci as any)?.baseFieldId ?? '').trim()
    const baseFieldKey = baseFieldIdRaw ? normalizeTableauFieldKey(baseFieldIdRaw) : ''
    const baseFieldClean = baseFieldIdRaw ? cleanBracketedFieldRef(baseFieldIdRaw) : ''
    const baseIsCalc =
      Boolean(baseFieldKey) &&
      (
        baseFieldKey === 'calculation' ||
        baseFieldKey.startsWith('calculation_') ||
        calculatedColKeys.has(baseFieldKey)
      )
    const targetBase = (baseIsCalc && baseFieldClean) ? baseFieldClean : base
    const nm = String(ci?.name ?? '').trim()
    if (nm) put(nm, targetBase, 130)
    if (baseFieldIdRaw) put(baseFieldIdRaw, targetBase, 120)
    const token = nm ? normalizeTableauFieldToken(nm) : ''
    if (token && token !== nm) put(token, targetBase, 110)
  }

  put(':Measure Names', 'Measure Names', 1000)
  put('Measure Names', 'Measure Names', 1000)
  put(':Measure Values', 'Measure Values', 1000)
  put('Measure Values', 'Measure Values', 1000)
  put(':Multiple Values', 'Measure Values', 1000)
  put('Multiple Values', 'Measure Values', 1000)

  const flat = new Map<string, string>()
  for (const [k, v] of out.entries()) flat.set(k, v.col)
  if (workbookIr && typeof workbookIr === 'object') {
    workbookInternalFieldMapCache.set(workbookIr as object, flat)
  }
  return flat
}

export function buildAvailableColumnMap(columns: string[] | null | undefined): Map<string, string> {
  if (columns && typeof columns === 'object') {
    const cached = availableColumnMapCache.get(columns as object)
    if (cached) return cached
  }
  const out = new Map<string, { col: string; score: number }>()
  const put = (keyRaw: string, col: string, score: number) => {
    const k = String(keyRaw ?? '').trim().toLowerCase()
    if (!k) return
    const prev = out.get(k)
    if (!prev || score > prev.score) out.set(k, { col, score })
  }

  const cols = Array.isArray(columns) ? columns : []
  for (const c of cols) {
    const col = String(c ?? '').trim()
    if (!col) continue
    // Exact (lowercased)
    put(col, col, 100)
    // Tableau-style normalized key
    const k = normalizeTableauFieldKey(col)
    if (k) put(k, col, 90)
    // Token-normalized (keep copy noise)
    const kn = normalizeTableauFieldToken(cleanBracketedFieldRef(col)).trim().toLowerCase()
    if (kn) put(kn, col, 80)
  }

  const flat = new Map<string, string>()
  for (const [k, v] of out.entries()) flat.set(k, v.col)
  if (columns && typeof columns === 'object') {
    availableColumnMapCache.set(columns as object, flat)
  }
  return flat
}

export function buildManifestColumnMap(manifest: ExtractFieldManifest | null | undefined): Map<string, string> {
  if (manifest && typeof manifest === 'object') {
    const cached = manifestColumnMapCache.get(manifest as object)
    if (cached) return cached
  }
  const out = new Map<string, { col: string; score: number }>()
  const put = (keyRaw: string, col: string, score: number) => {
    const k = String(keyRaw ?? '').trim().toLowerCase()
    if (!k) return
    const prev = out.get(k)
    if (!prev || score > prev.score) out.set(k, { col, score })
  }
  const isCalcToken = (raw: string): boolean => {
    const cleaned = cleanBracketedFieldRef(raw)
    const tok = normalizeTableauFieldToken(cleaned)
    const key = normalizeTableauFieldKey(tok)
    if (!key) return false
    return key === 'calculation' || key.startsWith('calculation_')
  }
  const fields = Array.isArray(manifest?.fields) ? manifest!.fields! : []
  for (const f of fields) {
    const col = String(f?.column_name ?? '').trim()
    if (!col) continue
    const fieldId = String(f?.field_id ?? '').trim()
    const baseFieldId = String((f as any)?.base_field_id ?? '').trim()
    const token = String(f?.token ?? '').trim()
    const caption = String(f?.caption ?? '').trim()
    const sourceKind = String((f as any)?.source_kind ?? '').trim().toLowerCase()
    const isCalcField =
      isCalcToken(fieldId) ||
      isCalcToken(baseFieldId) ||
      normalizeTableauFieldKey(token) === 'calculation'
    // Skip calc column-instances that point at base physical columns (e.g. calcs -> Sales).
    if (isCalcField && sourceKind === 'column_instance') continue
    if (fieldId) {
      put(fieldId, col, 100)
      const k = normalizeTableauFieldKey(fieldId)
      if (k) put(k, col, 95)
    }
    if (token) {
      put(token, col, 92)
      const k = normalizeTableauFieldKey(token)
      if (k) put(k, col, 90)
    }
    if (caption) {
      put(caption, col, 80)
      const k = normalizeTableauFieldKey(caption)
      if (k) put(k, col, 70)
    }
  }
  const flat = new Map<string, string>()
  for (const [k, v] of out.entries()) flat.set(k, v.col)
  if (manifest && typeof manifest === 'object') {
    manifestColumnMapCache.set(manifest as object, flat)
  }
  return flat
}

const normalizeManifestCalcIdentity = (raw: string): string => {
  const text = String(raw ?? '').trim()
  if (!text) return ''
  const unquoted = text
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return (
    normalizeTableauFieldKey(unquoted) ||
    normalizeTableauFieldKey(text) ||
    unquoted.toLowerCase() ||
    text.toLowerCase()
  )
}

export function buildManifestCalcIdentitySet(manifest: ExtractFieldManifest | null | undefined): Set<string> {
  if (manifest && typeof manifest === 'object') {
    const cached = manifestCalcIdentityCache.get(manifest as object)
    if (cached) return cached
  }
  const out = new Set<string>()
  const fields = Array.isArray(manifest?.fields) ? manifest.fields : []
  for (const field of fields) {
    const sourceKind = String((field as any)?.source_kind ?? '').trim().toLowerCase()
    const hasCalcPayload = Boolean(
      String((field as any)?.calc_formula_raw ?? '').trim() ||
      String((field as any)?.calc_formula ?? '').trim() ||
      String((field as any)?.calc_duckdb_sql ?? '').trim() ||
      (field as any)?.is_calc === true ||
      sourceKind.includes('calculation')
    )
    if (!hasCalcPayload) continue
    const identities = [
      String((field as any)?.field_id ?? '').trim(),
      String((field as any)?.token ?? '').trim(),
      String((field as any)?.caption ?? '').trim(),
      String((field as any)?.calc_formula_raw ?? '').trim(),
      String((field as any)?.calc_formula ?? '').trim(),
    ]
    for (const identity of identities) {
      const key = normalizeManifestCalcIdentity(identity)
      if (key) out.add(key)
    }
  }
  if (manifest && typeof manifest === 'object') {
    manifestCalcIdentityCache.set(manifest as object, out)
  }
  return out
}

export function manifestContainsResolvableCalcField(
  manifest: ExtractFieldManifest | null | undefined,
  field: string,
): boolean {
  const key = normalizeManifestCalcIdentity(field)
  if (!key) return false
  return buildManifestCalcIdentitySet(manifest).has(key)
}

export function resolveFieldToAvailableColumn(opts: {
  field: string
  aliases?: string[] | null
  workbookIr?: any | null
  availableColumns?: string[] | null
  fieldManifest?: ExtractFieldManifest | null
  allowFallback?: boolean
}): string {
  const field = String(opts.field ?? '').trim()
  if (!field) return ''
  const allowFallback = !FAIL_FAST_FIELD_MAPPING && opts.allowFallback !== false
  const debug = isFieldMappingDebugEnabled()
  const verboseDebug = debug && isFieldMappingVerboseEnabled()
  const availableCols = Array.isArray(opts.availableColumns) ? opts.availableColumns : []
  const available = buildAvailableColumnMap(opts.availableColumns)
  const manifest = buildManifestColumnMap(opts.fieldManifest)

  if (debug && !verboseDebug && !_resolveFieldVerboseNoticeShown) {
    _resolveFieldVerboseNoticeShown = true
    if (isFieldMappingConsoleEnabled()) {
      logFieldMapping(
        'resolveFieldToAvailableColumn detailed logs are suppressed. Add field_mapping_debug_verbose=1 to enable per-field resolver logs.'
      )
    }
  }

  const cleaned = cleanBracketedFieldRef(field)
  const normTok = normalizeTableauFieldToken(cleaned)
  const parsedRef = parseTableauFieldRef(field)
  const parsedToken = parsedRef?.token ? (cleanBracketedFieldRef(parsedRef.token) || parsedRef.token) : ''
  const aliasCandidates = Array.isArray(opts.aliases)
    ? opts.aliases
      .map((raw) => String(raw ?? '').trim())
      .filter(Boolean)
    : []
  const internalAliasMap = buildWorkbookInternalFieldToBaseColumnMap(opts.workbookIr)
  const strictBase = (() => {
    for (const candidate of [field, cleaned, parsedToken, normTok, ...aliasCandidates]) {
      const k = normalizeTableauFieldKeyStrict(candidate)
      if (!k) continue
      const hit = internalAliasMap.get(k)
      if (hit) return hit
    }
    return null
  })()
  // 1) Try workbook alias -> base column id
  const aliasMap = buildWorkbookFieldAliasToBaseColumnMap(opts.workbookIr)
  const key = normalizeTableauFieldKey(field)
  const base = strictBase || (key ? (aliasMap.get(key) || null) : null)

  const generatedBase = (() => {
    const m = field.match(/^(.+?)\s*\(\s*generated\s*\)\s*$/i)
    return m && m[1] ? String(m[1]).trim() : ''
  })()
  const parsedDatasourceKey = parsedRef?.datasource ? normalizeTableauDatasourceKey(parsedRef.datasource) : ''
  const isCalcToken = /^calculation_\d+$/i.test(String(normTok || '').trim())
  const looksInternal =
    /:(nk|ok|qk|sk)(:\d+)?$/i.test(field) ||
    /^(none|usr|twk|win|attr|sum|avg|min|max|count|countd|ctd|tqr|tyr|tmn|twk|tdy|yr|mn|wk|dy|qr|pcto):/i.test(field) ||
    /_\\d{8,}$/i.test(field) ||
    /\(\s*copy/i.test(field) ||
    /:(nk|ok|qk|sk)(:\d+)?$/i.test(cleaned) ||
    /_\\d{8,}$/i.test(cleaned) ||
    /\(\s*copy/i.test(cleaned)
  const remapMapRaw = opts.workbookIr && typeof opts.workbookIr === 'object'
    ? (opts.workbookIr as any)?.meta?.fusegrid_field_remaps_v1
    : null
  const remapMap: Record<string, string> =
    remapMapRaw && typeof remapMapRaw === 'object' ? (remapMapRaw as Record<string, string>) : {}
  const remapLookupKeys = new Set<string>()
  const pushRemapKey = (raw: unknown) => {
    const str = String(raw ?? '').trim()
    if (!str) return
    remapLookupKeys.add(str)
    remapLookupKeys.add(str.toLowerCase())
    const nk = normalizeTableauFieldKey(str)
    if (nk) remapLookupKeys.add(nk)
  }
  pushRemapKey(field)
  pushRemapKey(cleaned)
  pushRemapKey(normTok)
  pushRemapKey(base)
  for (const alias of aliasCandidates) pushRemapKey(alias)
  const remapTarget = (() => {
    for (const rk of remapLookupKeys) {
      const dsScoped = parsedDatasourceKey ? `${parsedDatasourceKey}::${rk}` : ''
      const hitScoped = dsScoped ? String((remapMap as any)?.[dsScoped] ?? '').trim() : ''
      if (hitScoped) return hitScoped
      const hit = String((remapMap as any)?.[rk] ?? '').trim()
      if (hit) return hit
    }
    return ''
  })()
  const remapCandidates = remapTarget
    ? [
        remapTarget,
        cleanBracketedFieldRef(remapTarget),
        normalizeTableauFieldToken(remapTarget),
      ]
    : []
  const provenanceSourceCandidates = [
    field,
    cleaned,
    normTok,
    ...aliasCandidates,
  ]
  const provenanceCandidates = provenanceSourceCandidates
    .map((candidate) => stripDisplayProvenanceSuffix(candidate))
    .filter((candidate, idx) => Boolean(candidate) && candidate !== provenanceSourceCandidates[idx])
  const candidates = Array.from(new Set([
    ...remapCandidates,
    field,
    generatedBase || null,
    cleaned,
    normTok,
    ...aliasCandidates,
    ...provenanceCandidates,
    strictBase,
    ...(!looksInternal && !isCalcToken ? [stripTableauAutoGeneratedSuffixes(normTok)] : []),
    ...((looksInternal && !strictBase) ? [] : [base]),
  ]))
    .map((x) => String(x ?? '').trim())
    .filter(Boolean)

  const baseTrace = debug
    ? {
        phase: 'resolve_field',
        message: 'resolveFieldToAvailableColumn',
        data: {
          field,
          aliases: aliasCandidates,
          base,
          remapTarget: remapTarget || null,
          candidates,
          availableCount: availableCols.length,
          manifestCount: manifest.size,
          availableSample: availableCols.slice(0, 200),
        },
      }
    : null

  if (manifest.size > 0) {
    for (const c of candidates) {
      const k0 = c.toLowerCase()
      const k1 = normalizeTableauFieldKey(c)
      const hit0 = k0 && manifest.get(k0)
      const hit1 = k1 && manifest.get(k1)
      const hit = hit0 || hit1 || null
      if (hit) {
        if (debug && verboseDebug) {
          pushFieldMappingTrace({
            ...(baseTrace as any),
            message: 'matched manifest column',
            data: { ...(baseTrace as any)?.data, candidate: c, key: hit0 ? k0 : k1, resolved: hit },
          })
          logFieldMapping('resolveFieldToAvailableColumn: manifest match', { field, candidate: c, resolved: hit })
        }
        return hit
      }
    }
    const wantsGeneratedLatLon =
      /\(\s*generated\s*\)/i.test(field) &&
      (/\blatitude\b/i.test(field) || /\blongitude\b/i.test(field))
    if (wantsGeneratedLatLon) {
      const geom = available.get('geometry') || available.get('geom')
      if (geom) {
        if (debug && verboseDebug) {
          pushFieldMappingTrace({
            ...(baseTrace as any),
            message: 'generated_latlon_geometry',
            data: { ...(baseTrace as any)?.data, resolved: geom },
          })
          logFieldMapping('resolveFieldToAvailableColumn: generated lat/lon -> geometry', { field, resolved: geom })
        }
        return geom
      }
    }
    if (debug && verboseDebug && !allowFallback) {
      // Strict mode still permits deterministic direct matches against available columns.
      // We only suppress the final heuristic fallback at the end of resolution.
      pushFieldMappingTrace({
        ...(baseTrace as any),
        message: 'unresolved_manifest_continue_available',
        data: { ...(baseTrace as any)?.data, resolved: null },
      })
      logFieldMapping('resolveFieldToAvailableColumn: unresolved (manifest), checking available columns', { field })
    }
  }

  // If this field maps to a known workbook column with a caption, try matching available columns by caption.
  try {
    const fd = opts.workbookIr && typeof opts.workbookIr === 'object' ? (opts.workbookIr as any).field_dictionary : null
    const cols: any[] = Array.isArray(fd?.columns) ? fd.columns : []
    const baseKey = normalizeTableauFieldKey(base || field)
    const hitCol = cols.find((c) => normalizeTableauFieldKey(String(c?.name ?? '')) === baseKey) || null
    const cap = String(hitCol?.caption ?? '').trim()
    if (cap) {
      const capKey = normalizeTableauFieldKey(cap)
      for (const [k, v] of available.entries()) {
        const kk = String(k || '').trim().toLowerCase()
        if (!kk) continue
        if (kk === cap.toLowerCase() || (capKey && kk === capKey)) {
          if (debug && verboseDebug) {
            pushFieldMappingTrace({
              ...(baseTrace as any),
              message: 'matched caption',
              data: { ...(baseTrace as any)?.data, caption: cap, resolved: v },
            });
            logFieldMapping('resolveFieldToAvailableColumn: caption match', { field, caption: cap, resolved: v });
          }
          return v
        }
      }
    }
  } catch { }

  // 2) If we have a known set of physical columns, snap to it deterministically.
  for (const c of candidates) {
    const k0 = c.toLowerCase()
    const k1 = normalizeTableauFieldKey(c)
    const hit0 = k0 && available.get(k0)
    const hit1 = k1 && available.get(k1)
    const hit = hit0 || hit1 || null
    if (hit) {
      if (debug && verboseDebug) {
        pushFieldMappingTrace({
          ...(baseTrace as any),
          message: 'matched available column',
          data: { ...(baseTrace as any)?.data, candidate: c, key: hit0 ? k0 : k1, resolved: hit },
        });
        logFieldMapping('resolveFieldToAvailableColumn: available match', { field, candidate: c, resolved: hit });
      }
      return hit
    }
  }

  if (!allowFallback) {
    if (debug && verboseDebug) {
      pushFieldMappingTrace({
        ...(baseTrace as any),
        message: 'unresolved',
        data: { ...(baseTrace as any)?.data, resolved: null },
      })
      logFieldMapping('resolveFieldToAvailableColumn: unresolved', { field })
    }
    return ''
  }

  // 3) Fallback to the base or raw cleaned field.
  const fallback = base || cleanBracketedFieldRef(field) || field
  if (debug && verboseDebug) {
    pushFieldMappingTrace({
      ...(baseTrace as any),
      message: 'fallback',
      data: { ...(baseTrace as any)?.data, resolved: fallback },
    })
    logFieldMapping('resolveFieldToAvailableColumn: fallback', { field, resolved: fallback })
  }
  return fallback
}

export function resolveExtractColumnName(opts: {
  fieldName: string
  aliases?: string[] | null
  workbookIr?: any | null
  availableColumns?: string[] | null
  fieldManifest?: ExtractFieldManifest | null
  allowFallback?: boolean
}): string {
  const fieldName = String(opts.fieldName ?? '').trim()
  if (!fieldName) return ''
  const manifestCols = opts.fieldManifest && Array.isArray(opts.fieldManifest.extract_columns)
    ? opts.fieldManifest.extract_columns
    : null
  const availableColumns = (Array.isArray(opts.availableColumns) && opts.availableColumns.length)
    ? opts.availableColumns
    : manifestCols
  const resolved = resolveFieldToAvailableColumn({
    field: fieldName,
    aliases: opts.aliases ?? null,
    workbookIr: opts.workbookIr ?? null,
    availableColumns,
    fieldManifest: opts.fieldManifest ?? null,
    allowFallback: opts.allowFallback === true,
  })
  if (resolved) return resolved
  if (FAIL_FAST_FIELD_MAPPING) return ''
  return cleanBracketedFieldRef(fieldName) || fieldName
}
